import { definePreset } from '@primeng/themes';
import Material from '@primeng/themes/material';

import { PortalType } from 'sc-common/core/models/portal-type';

// Scientific brand colors (red theme)
const ScientificColors = {
    primary: {
        50: '#fef2f2',   // Very light red tint
        100: '#fee2e2',  // Light red tint
        200: '#fecaca',  // Lighter red
        300: '#fca5a5',  // Light red
        400: '#f87171',  // Medium-light red
        500: '#7f1418',  // Brand red (main color)
        600: '#6b1114',  // Darker red
        700: '#570e10',  // Even darker
        800: '#430b0d',  // Very dark red
        900: '#2f0809',  // Almost black red
        950: '#1b0505'   // Near black
    },
    hoverColor: '#a90003',
    fontFamily: 'Roboto, Helvetica Neue Light, Helvetica Neue, Helvetica, Arial, Lucida Grande, sans-serif'
};

// AcademicOA brand colors (green theme)
const AcademicoaColors = {
    primary: {
        50: '#e6faf4',   // Very light green tint
        100: '#c3f3e5',  // Light green tint
        200: '#9aecd5',  // Lighter green
        300: '#6be3c3',  // Light green
        400: '#3fd9b0',  // Medium-light green
        500: '#17c294',  // Brand green (main color)
        600: '#13a67e',  // Darker green
        700: '#0f8a68',  // Even darker
        800: '#0b6e52',  // Very dark green
        900: '#07523d',  // Almost black green
        950: '#033627'   // Near black
    },
    hoverColor: '#06a57a',
    fontFamily: '"IBM Plex Sans", sans-serif'
};

/**
 * Creates a PrimeNG theme preset for the specified portal.
 * @param portal - The portal type ('scientific' or 'academicoa')
 * @returns PrimeNG theme preset configured with portal-specific colors
 */
export function createExternalThemePreset(portal: PortalType): ReturnType<typeof definePreset> {
    const colors = portal === 'academicoa' ? AcademicoaColors : ScientificColors;

    return definePreset(Material, {
        primitive: {
            borderRadius: {
                none: '0',
                xs: '0',
                sm: '0',
                md: '0',
                lg: '0',
                xl: '0'
            }
        },
        components: {
            floatlabel: {
                // fontWeight removed in PrimeNG 20 - use CSS overrides if needed
            }
        },
        semantic: {
            // fontFamily removed in PrimeNG 20 - use CSS --font-family variable instead
            primary: colors.primary,
            colorScheme: {
                light: {
                    primary: {
                        color: '{primary.500}',
                        contrastColor: '#ffffff',
                        hoverColor: colors.hoverColor,
                        activeColor: '{primary.700}'
                    },
                    highlight: {
                        background: '{primary.50}',
                        focusBackground: '{primary.100}',
                        color: '{primary.700}',
                        focusColor: '{primary.800}'
                    }
                }
            }
        }
    });
}

// Default export for backward compatibility (Scientific theme)
const ExternalThemePreset = createExternalThemePreset('scientific');
export default ExternalThemePreset;
